
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TabsContent } from "@/components/ui/tabs";
import { CheckCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useUserBusiness } from "@/hooks/staff/useUserBusiness";
import { staffApiClient } from "@/api/staff";
import { useStaffConverter } from "@/hooks/staff/useStaffConverter";
import { logger } from "@/lib/logger";
import LoadingState from "./common/LoadingState";
import NoStaffWarning from "./warnings/NoStaffWarning";
import TabsContainer from "./tabs/TabsContainer";
import { getIntegrationsByCategory } from "./registry/IntegrationsRegistry";

const CommunicationChannels: React.FC = () => {
  const [activeTab, setActiveTab] = useState("slack");
  const { fetchCurrentUser, fetchBusinessId } = useUserBusiness();
  const { convertStaffData } = useStaffConverter();
  
  // Get current user
  const { data: userId, isLoading: isUserLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
  
  // Get business ID only if we have a user ID
  const { data: businessId, isLoading: isBusinessLoading } = useQuery({
    queryKey: ['businessId', userId],
    queryFn: () => fetchBusinessId(userId!),
    enabled: !!userId,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Fetch staff data only if we have a business ID
  const { data: staffData, isLoading: isStaffLoading } = useQuery({
    queryKey: ['primaryAiStaff', businessId],
    queryFn: async () => {
      if (!businessId) return null;
      
      try {
        const allStaff = await staffApiClient.fetchAllStaff(businessId);
        if (!allStaff || allStaff.length === 0) return null;
        return convertStaffData(allStaff[0]);
      } catch (error) {
        logger.error({
          message: "Error fetching primary AI staff",
          error,
          context: { businessId }
        });
        return null;
      }
    },
    enabled: !!businessId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Loading state
  const isLoading = isUserLoading || isBusinessLoading || isStaffLoading;

  if (isLoading) {
    return <LoadingState />;
  }

  const aiStaffId = staffData?.id || "sample-1";
  const aiStaffName = staffData?.name || "Your AI Assistant";

  // Get available communication integrations from registry
  const communicationIntegrations = getIntegrationsByCategory("communication");
  
  // Create tab items from registered integrations
  const tabs = communicationIntegrations.map(integration => ({
    id: integration.id,
    icon: integration.icon,
    label: integration.name,
    disabled: !integration.isAvailable,
    badgeText: integration.isAvailable && staffData ? "Available" : undefined,
    badgeVariant: "default" as const
  }));
  
  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Communication Platforms</CardTitle>
          <CardDescription>Connect your AI assistants to the messaging platforms your team uses daily</CardDescription>
        </CardHeader>
        <CardContent>
          <NoStaffWarning showWarning={!staffData} />
          
          {!staffData ? null : (
            <div className="mt-4">
              <TabsContainer 
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabs={tabs}
              >
                {communicationIntegrations.map(integration => (
                  <TabsContent key={integration.id} value={integration.id}>
                    {integration.isAvailable ? (
                      <integration.component 
                        aiStaffId={aiStaffId} 
                        aiStaffName={aiStaffName} 
                      />
                    ) : (
                      <Card>
                        <CardContent className="py-6 text-center">
                          <h3 className="text-lg font-medium mb-2">{integration.name} - Coming Soon</h3>
                          <p className="text-muted-foreground">
                            We're working on integrating {integration.name}. Check back later!
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>
                ))}
              </TabsContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommunicationChannels;
